import type { FunctionComponent } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useParams } from "wouter-preact";
import HeaderNav from "../../components/header_nav/header_nav.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { navigate } from "../../router_utils.ts";

const TZ_LIST = [
    "Asia/Shanghai",
    "Etc/UTC",
    "Etc/GMT+1",
    "Etc/GMT+2",
    "Etc/GMT+3",
    "Etc/GMT+4",
    "Etc/GMT+5",
    "Etc/GMT+6",
    "Etc/GMT+7",
    "Etc/GMT+8",
    "Etc/GMT+9",
    "Etc/GMT+10",
    "Etc/GMT+11",
    "Etc/GMT+12",
    "Etc/GMT-1",
    "Etc/GMT-2",
    "Etc/GMT-3",
    "Etc/GMT-4",
    "Etc/GMT-5",
    "Etc/GMT-6",
    "Etc/GMT-7",
    "Etc/GMT-8",
    "Etc/GMT-9",
    "Etc/GMT-10",
    "Etc/GMT-11",
    "Etc/GMT-12",
    "Etc/GMT-13",
    "Etc/GMT-14",
];

const setCookie = (name: string, value: string, maxAgeDays: number) => {
    const maxAge = Math.floor(maxAgeDays * 24 * 60 * 60);
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/`;
};

const getCookie = (name: string) => {
    const cookies = document.cookie.split(";");
    for (const c of cookies) {
        const [k, ...rest] = c.trim().split("=");
        if (k === name) return decodeURIComponent(rest.join("="));
    }
    return "";
};

export const PreviewUserSettingPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const params = useParams();
    const link = params["link"] ?? "";
    const [theme, setTheme] = useState(getCookie("theme") || "");
    const [tz, setTz] = useState(getCookie("tz") || "");

    useEffect(() => {
        document.title = t({ zh: "预览设置", en: "Preview Settings" });
    }, [t]);

    const applyThemeAction = useMemo(() => async () => {
        setCookie("theme", theme, 180);
        if (theme) {
            document.documentElement.dataset.theme = theme;
        } else {
            delete (document.documentElement.dataset as Record<string, string>)["theme"];
        }
        toast(t({ zh: "主题已应用", en: "Theme applied" }), "success");
    }, [theme, toast, t]);

    const applyTzAction = useMemo(() => async () => {
        setCookie("tz", tz, 180);
        toast(t({ zh: "时区已保存", en: "Timezone saved" }), "success");
    }, [tz, toast, t]);

    return (
        <div>
            <HeaderNav title={t({ zh: "预览设置", en: "Preview Settings" })} />
            <div class="container farpov_top_container">
                <button type="button" class="secondary" onClick={() => navigate(`/preview/${encodeURIComponent(link)}`, { replace: false })}>
                    {t({ zh: "返回预览", en: "Back" })}
                </button>
                <hr />
                <label>
                    {t({ zh: "主题", en: "Theme" })}
                    <select value={theme} onChange={(e) => setTheme((e.target as HTMLSelectElement).value)}>
                        <option value="">{t({ zh: "跟随默认", en: "Default" })}</option>
                        <option value="light">{t({ zh: "浅色", en: "Light" })}</option>
                        <option value="dark">{t({ zh: "深色", en: "Dark" })}</option>
                    </select>
                </label>
                <button type="button" class="secondary" onClick={applyThemeAction}>
                    {t({ zh: "应用主题", en: "Apply Theme" })}
                </button>
                <label style="margin-top: var(--pico-spacing);">
                    {t({ zh: "时区", en: "Timezone" })}
                    <select value={tz} onChange={(e) => setTz((e.target as HTMLSelectElement).value)}>
                        <option value="">{t({ zh: "跟随浏览器", en: "Browser default" })}</option>
                        {TZ_LIST.map((z) => (
                            <option key={z} value={z}>
                                {z}
                            </option>
                        ))}
                    </select>
                </label>
                <button type="button" class="secondary" onClick={applyTzAction}>
                    {t({ zh: "保存时区", en: "Save Timezone" })}
                </button>
            </div>
        </div>
    );
};

export default PreviewUserSettingPage;
